import { mapState } from 'vuex';
import {
  ApartmentDTO,
  ApartmentLabelDTO,
  EntrancePlantDetailDTO,
} from '@/../client-generator/generated-clients/plants';
import { PropType } from 'vue';
import { Options, Vue } from 'vue-class-component';
import plantsClient from '@/core/services/plants';
import { SET_APP_LOADING, SET_SELECTED_ENTRANCE_PLANT } from '@/core/store/action-types';
import CodeSection from './CodeSection/CodeSection.vue';
import BadgesSection from './BadgesSection/BadgesSection.vue';
import ContactSection from './ContactSection/ContactSection.vue';
import GateAccessSection from './GateAccessSection/GateAccessSection.vue';

@Options({
  name: 'ResidetDetailsEdit',
  props: {
    apartmentSelected: {
      type: Object as PropType<ApartmentDTO>,
      required: true,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  components: {
    ContactSection,
    GateAccessSection,
    BadgesSection,
    CodeSection,
  },
  emits: ['update-list'],
})
export default class ResidetDetailsEdit extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  apartmentSelected!: ApartmentDTO;
  editedApartment: ApartmentDTO = new ApartmentDTO();

  contactEdited = false;
  gateAccessEdited = false;
  badgeEdited = false;
  keyCodeEdited = false;
  errorContact = false;
  errorGateAccess = false;
  errorBadge = false;
  errorKeyCode = false;

  gatesEditedIds: string[] = [];

  created() {
    this.initApartmentToEdit();
  }

  get apartmentWasEdited() {
    const tabEdited =
      this.contactEdited ||
      this.gateAccessEdited ||
      this.badgeEdited ||
      this.keyCodeEdited;

    const tabHasError =
      this.errorContact ||
      this.errorGateAccess ||
      this.errorBadge ||
      this.errorKeyCode;

    return tabEdited && !tabHasError;
  }

  get saveDisabled() {
    if(!this.currentEntrancePlant.syncStatus?.canEditWithPropagation) return true;

    return !this.apartmentWasEdited;
  }

  get gates() {
    return this.currentEntrancePlant.gates;
  }

  initApartmentToEdit() {
    this.editedApartment = new ApartmentDTO(this.apartmentSelected);
  }

  handleContactEdited(value: boolean) {
    this.contactEdited = value;
  }

  handleGateAccessEdited(value: boolean, gates: string[]) {
    this.gateAccessEdited = value;
    this.gatesEditedIds = gates;
  }

  handleBadgeEdited(value: boolean) {
    this.badgeEdited = value;
  }

  handleKeyCodeEdited(value: boolean, keyCode: string) {
    this.keyCodeEdited = value;
    if (!this.errorKeyCode) {
      this.editedApartment.keypadCode = keyCode;
    }
  }

  handleErrorContact(value: boolean) {
    this.errorContact = value;
  }

  handleErrorGateAccess(value: boolean) {
    this.errorGateAccess = value;
  }

  handleErrorBadge(value: boolean) {
    this.errorBadge = value;
  }

  handleErrorKeyCode(value: boolean) {
    this.errorKeyCode = value;
  }

  handleUpdateList() {
    this.$emit('update-list');
  }

  async onAddEditLabel(newLabel: ApartmentLabelDTO) {
    this.$store.dispatch(SET_APP_LOADING, true);
    if (!this.apartmentSelected) return;

    // add label to the list
    try {
      let l: ApartmentLabelDTO[] = [];
      if (newLabel.labelId) {
        l = await plantsClient.labelPUT(
          this.currentEntrancePlant.desktopId!,
          this.apartmentSelected.apartmentId!,
          newLabel
        );
      } else {
        l = await plantsClient.labelPOST(
          this.currentEntrancePlant.desktopId!,
          this.apartmentSelected.apartmentId!,
          newLabel
        );
      }
      if (!this.apartmentSelected.labels) this.apartmentSelected.labels = [];
      this.apartmentSelected.labels.splice(
        0,
        this.apartmentSelected.labels.length,
        ...l
      );
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
    } finally {
      this.$emit('update-list');
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  async onDeleteLabel(labelId: string) {
    this.$store.dispatch(SET_APP_LOADING, true);
    if (!this.apartmentSelected) return;

    // delete label
    if (labelId) {
      try {
        const l = await plantsClient.labelDELETE(
          this.currentEntrancePlant.desktopId!,
          this.apartmentSelected.apartmentId!,
          labelId
        );
        if (!this.apartmentSelected.labels) this.apartmentSelected.labels = [];
        this.apartmentSelected.labels.splice(
          0,
          this.apartmentSelected.labels.length,
          ...l
        );
        const plant = await plantsClient.entranceplantGET(
          this.currentEntrancePlant.desktopId!,
          undefined
        );
        this.$store.dispatch(SET_SELECTED_ENTRANCE_PLANT, plant);
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('general.operationError'),
          closable: true,
          life: 6000,
        });
      } finally {
        this.$store.dispatch(SET_APP_LOADING, false);
      }
    }
  }


  async onSaveClick() {
    this.$store.dispatch(SET_APP_LOADING, true);

    try {
      this.replaceApartment(this.currentEntrancePlant, this.editedApartment);
      this.updatePlantGates();
      await plantsClient.residents(
        this.currentEntrancePlant.desktopId!,
        this.currentEntrancePlant
      );

      this.$emit('update-list');
      this.$toast.add({
        severity: 'success',
        summary: this.$t('AppResource.Saved'),
        closable: true,
        life: 6000,
      });
      this.restoreEditCheckers();
    } catch (error) {
      console.error(error);
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  replaceApartment(plant: EntrancePlantDetailDTO, editedApartment: ApartmentDTO) {
    const buildingIndex = plant.buildings!.findIndex(
      (b) => b.buildingId === editedApartment.buildingId
    );
    const building = plant.buildings![buildingIndex];
    const apartmentIndex = building.apartments!.findIndex(
      (a) => a.apartmentId === editedApartment.apartmentId
    );
    building.apartments!.splice(apartmentIndex, 1, editedApartment);

    plant.buildings!.splice(buildingIndex, 1, building);
  }

  updatePlantGates() {
    if(this.gatesEditedIds.includes('all_residents')) {
      this.gatesEditedIds = this.gatesEditedIds.filter(g => g !== 'all_residents');
      this.currentEntrancePlant.buildings!.find(b => b.buildingId === this.apartmentSelected.buildingId)?.apartments?.forEach(a => {
        this.updateApartmentGates(a);
      });
    }
    else {
      this.updateApartmentGates(this.apartmentSelected);
    }
  }

  updateApartmentGates(apartmentToAdd: ApartmentDTO) {
    if (this.currentEntrancePlant.gates) {
      for (const gate of this.currentEntrancePlant.gates) {
        if (gate.apartments?.includes(apartmentToAdd.apartmentId!)) {
          gate.apartments = gate.apartments.filter(
            (c) => c !== apartmentToAdd.apartmentId
          );
        }
      }
      // Aggiungo l'appartamento ai gate selezionati
      for (const gateId of this.gatesEditedIds) {
        const gate = this.currentEntrancePlant.gates.find(
          (c) => c.gateId === gateId
        );
        if (gate) {
          gate.apartments!.push(apartmentToAdd.apartmentId!);
        }
      }
    }

  }

  restoreEditCheckers() {
    this.contactEdited = false;
    this.gateAccessEdited = false;
    this.badgeEdited = false;
    this.keyCodeEdited = false;
    this.errorContact = false;
    this.errorGateAccess = false;
    this.errorBadge = false;
    this.errorKeyCode = false;
  }

}